import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Typography } from 'antd';
import Map, { Layer, Source, MapRef, useMap } from 'react-map-gl';
import { generateHexagonGrid, binPointsToHexagons } from '../../utils/hexagonGrid';
import 'mapbox-gl/dist/mapbox-gl.css';

interface Props {
    title?: React.ReactNode;
    mode: 'template' | 'gps';
    templateSize?: { width: number; height: number };
    geoJsonPreview?: any;
    templateImageUrl?: string;
    templateLinesUrl?: string; // NEW: Transparent PNG with field lines only
    subtitle?: React.ReactNode;
    value?: number | null;
    unit?: string;
    dataPoints?: { x: number; y: number; value: number; gpsLat?: number; gpsLon?: number }[];
    grid?: {
        resolutionX: number;
        resolutionY: number;
        cells: Array<{ x: number; y: number; value: number | null }>;
    };
    preBinnedHexagons?: Array<{ coordinates: Array<[number, number]>; value: number; count: number }>; // Pre-binned hexagons from backend
    templateOpacity?: number; // 0-1
    heatmapOpacity?: number; // 0-1  
    colorScheme?: 'blue-red' | 'green-red' | 'viridis' | 'yellow-red' | 'red-yellow-green';
    invertColors?: boolean; // Whether to invert the color scheme
    valueRange?: {
        mode: 'auto' | 'fixed';
        min?: number;
        max?: number;
    };
    hexagonSize?: number; // Size in CENTIMETERS for GPS hexagon heatmap (converted to meters internally)
    showCard?: boolean; // Whether to wrap in Card with title (default true)
}

const formatNumber = (v?: number | null) => {
    if (v === null || v === undefined || Number.isNaN(v)) return '—';
    return Number(v).toFixed(2);
};

export const ReportHeatmap: React.FC<Props> = ({ 
    title, 
    mode, 
    templateSize, 
    geoJsonPreview, 
    templateImageUrl,
    templateLinesUrl,
    subtitle, 
    value, 
    unit, 
    dataPoints,
    grid,
    preBinnedHexagons,
    templateOpacity = 0.7,
    colorScheme = 'blue-red',
    invertColors = false,
    valueRange,
    heatmapOpacity = 0.9,
    hexagonSize = 50, // Default 50 cm (0.5 meters)
    showCard = true
}) => {
    const imgRef = useRef<HTMLImageElement>(null);
    const linesImgRef = useRef<HTMLImageElement>(null);
    const [imgLoaded, setImgLoaded] = useState(false);
    const [linesImgLoaded, setLinesImgLoaded] = useState(false);
    const mapRefGps = useRef<MapRef>(null);

    // Calculate map bearing for auto-rotation (align left edge/short side vertical)
    const mapBearing = useMemo(() => {
        if (!geoJsonPreview?.features?.[0]?.geometry?.coordinates?.[0]) return 0;
        
        const coords = geoJsonPreview.features[0].geometry.coordinates[0];
        if (coords.length < 4) return 0;
        
        // Assuming rectangle: coords are [BL, BR, TR, TL, BL]
        // Calculate bearing of LEFT edge (BL to TL) - the short side
        const p1 = coords[0]; // Bottom-left [lon, lat]
        const p2 = coords[3]; // Top-left [lon, lat]
        
        // Use proper great-circle bearing formula (accounts for Earth's curvature)
        // Formula: θ = atan2(sin(Δλ) * cos(φ2), cos(φ1) * sin(φ2) − sin(φ1) * cos(φ2) * cos(Δλ))
        // Where φ = latitude, λ = longitude (all in radians)
        
        const toRadians = (deg: number) => deg * (Math.PI / 180);
        const toDegrees = (rad: number) => rad * (180 / Math.PI);
        
        const lat1 = toRadians(p1[1]);
        const lon1 = toRadians(p1[0]);
        const lat2 = toRadians(p2[1]);
        const lon2 = toRadians(p2[0]);
        
        const dLon = lon2 - lon1;
        
        // Calculate initial bearing (forward azimuth)
        const y = Math.sin(dLon) * Math.cos(lat2);
        const x = Math.cos(lat1) * Math.sin(lat2) - 
                  Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
        
        const bearingRad = Math.atan2(y, x);
        let geoBearing = toDegrees(bearingRad);
        
        // Normalize to 0-360
        geoBearing = (geoBearing + 360) % 360;
        
        // For Mapbox: to align the left edge vertically (pointing north),
        // we need to rotate the map by the NEGATIVE of the edge's bearing
        // This counter-rotates the edge to point north (bearing 0)
        let mapRotation = -geoBearing;
        
        // Normalize to -180 to 180
        if (mapRotation > 180) mapRotation -= 360;
        if (mapRotation < -180) mapRotation += 360;
        
        console.log('🧭 Map bearing calculation (great-circle):', { 
            p1: `[${p1[0].toFixed(8)}, ${p1[1].toFixed(8)}]`, 
            p2: `[${p2[0].toFixed(8)}, ${p2[1].toFixed(8)}]`,
            geoBearing: geoBearing.toFixed(2) + '°',
            mapRotation: mapRotation.toFixed(2) + '°',
            explanation: `Left edge has bearing ${geoBearing.toFixed(1)}° (true north), rotating map by ${mapRotation.toFixed(1)}° to align it vertically`,
            coords: coords.slice(0, 4) // Show first 4 coords for reference
        });
        
        return -mapRotation;
    }, [geoJsonPreview]);

    const { minValue, maxValue } = useMemo(() => {
        // Use fixed range if specified
        if (valueRange?.mode === 'fixed' && valueRange.min !== undefined && valueRange.max !== undefined) {
            return { minValue: valueRange.min, maxValue: valueRange.max };
        }
        
        // Otherwise auto-calculate from data
        if (grid && grid.cells.length > 0) {
            const values = grid.cells
                .map(c => c.value)
                .filter((v): v is number => v !== null && v !== undefined);
            
            if (values.length === 0) return { minValue: 0, maxValue: 100 };
            
            return {
                minValue: Math.min(...values),
                maxValue: Math.max(...values)
            };
        }
        
        if (dataPoints && dataPoints.length > 0) {
            const minVal = Math.min(...dataPoints.map(p => p.value));
            const maxVal = Math.max(...dataPoints.map(p => p.value));
            return { minValue: minVal, maxValue: maxVal };
        }
        
        return { minValue: 0, maxValue: 100 };
    }, [grid, dataPoints, valueRange]);

    const getColorForValue = (value: number): string => {
        const range = maxValue - minValue;
        if (range === 0) return 'rgb(128, 128, 128)';
        
        let normalized = Math.max(0, Math.min(1, (value - minValue) / range));
        
        // Apply color inversion if enabled
        if (invertColors) {
            normalized = 1 - normalized;
        }
        
        switch (colorScheme) {
            case 'blue-red': {
                // Blue (cold/low) to Red (hot/high)
                const r = Math.floor(normalized * 255);
                const g = 0;
                const b = Math.floor((1 - normalized) * 255);
                return `rgb(${r}, ${g}, ${b})`;
            }
            case 'green-red': {
                // Red (bad/low) to Green (good/high)
                const r = Math.floor((1 - normalized) * 255);
                const g = Math.floor(normalized * 255);
                const b = 0;
                return `rgb(${r}, ${g}, ${b})`;
            }
            case 'red-yellow-green': {
                // Red (bad/low) → Yellow (mid) → Green (good/high)
                // 0.0-0.5: Red to Yellow
                // 0.5-1.0: Yellow to Green
                let r, g, b;
                if (normalized < 0.5) {
                    // Red (255,0,0) → Yellow (255,255,0)
                    const t = normalized * 2; // 0 to 1
                    r = 255;
                    g = Math.floor(t * 255);
                    b = 0;
                } else {
                    // Yellow (255,255,0) → Green (0,255,0)
                    const t = (normalized - 0.5) * 2; // 0 to 1
                    r = Math.floor((1 - t) * 255);
                    g = 255;
                    b = 0;
                }
                return `rgb(${r}, ${g}, ${b})`;
            }
            case 'viridis': {
                // Perceptually uniform color scheme
                const viridisColors = [
                    [68, 1, 84], [72, 40, 120], [62, 73, 137], 
                    [49, 104, 142], [38, 130, 142], [31, 158, 137],
                    [53, 183, 121], [109, 205, 89], [180, 222, 44], [253, 231, 37]
                ];
                const idx = Math.floor(normalized * (viridisColors.length - 1));
                const [r, g, b] = viridisColors[idx];
                return `rgb(${r}, ${g}, ${b})`;
            }
            case 'yellow-red': {
                // Yellow (low) to Red (high)
                const r = 255;
                const g = Math.floor((1 - normalized) * 255);
                const b = 0;
                return `rgb(${r}, ${g}, ${b})`;
            }
            default: {
                // Default: light blue to dark blue
                const r = Math.floor(230 - (normalized * 210));
                const g = Math.floor(247 - (normalized * 167));
                const b = 255;
                return `rgb(${r}, ${g}, ${b})`;
            }
        }
    };

    const renderGridHeatmap = () => {
        if (!grid || grid.cells.length === 0) {
            return null;
        }

        const { resolutionX, resolutionY, cells } = grid;
        
        // Create a 2D array for easier rendering
        const gridData: Array<Array<number | null>> = Array(resolutionY)
            .fill(null)
            .map(() => Array(resolutionX).fill(null));
        
        cells.forEach(cell => {
            if (cell.y >= 0 && cell.y < resolutionY && cell.x >= 0 && cell.x < resolutionX) {
                const displayY = resolutionY - 1 - cell.y;
                gridData[displayY][cell.x] = cell.value;
            }
        });

        // Calculate the actual rendered image bounds for object-fit: contain
        let gridStyle: React.CSSProperties = {
            position: 'absolute',
            display: 'grid',
            gridTemplateRows: `repeat(${resolutionY}, 1fr)`,
            gridTemplateColumns: `repeat(${resolutionX}, 1fr)`,
            pointerEvents: 'none',
        };

        if (templateImageUrl && imgLoaded && imgRef.current) {
            const img = imgRef.current;
            const container = img.parentElement;
            if (container) {
                const containerWidth = container.clientWidth;
                const containerHeight = container.clientHeight;
                const imgAspect = img.naturalWidth / img.naturalHeight;
                const containerAspect = containerWidth / containerHeight;
                
                let renderedWidth, renderedHeight, offsetX, offsetY;
                
                if (imgAspect > containerAspect) {
                    // Image is wider - fits to container width
                    renderedWidth = containerWidth;
                    renderedHeight = containerWidth / imgAspect;
                    offsetX = 0;
                    offsetY = (containerHeight - renderedHeight) / 2;
                } else {
                    // Image is taller - fits to container height
                    renderedHeight = containerHeight;
                    renderedWidth = containerHeight * imgAspect;
                    offsetY = 0;
                    offsetX = (containerWidth - renderedWidth) / 2;
                }
                
                gridStyle = {
                    ...gridStyle,
                    top: offsetY,
                    left: offsetX,
                    width: renderedWidth,
                    height: renderedHeight,
                };
            }
        } else {
            // No template image - use full container
            gridStyle = {
                ...gridStyle,
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
            };
        }

        return (
            <div style={{ ...gridStyle, opacity: heatmapOpacity }}>
                {gridData.map((row, rowIndex) =>
                    row.map((cellValue, colIndex) => (
                        <div
                            key={`${rowIndex}-${colIndex}`}
                            style={{
                                backgroundColor: cellValue !== null 
                                    ? getColorForValue(cellValue) 
                                    : 'transparent',
                                border: 'none',
                            }}
                            title={cellValue !== null ? `Value: ${formatNumber(cellValue)}` : 'No data'}
                        />
                    ))
                )}
            </div>
        );
    };

    const hexagonGeoJson = useMemo(() => {
        // Skip hexagon binning entirely for template mode (uses grid instead)
        if (mode === 'template') {
            return null;
        }
        
        // Use pre-binned hexagons from backend if available (performance optimization)
        if (preBinnedHexagons && preBinnedHexagons.length > 0) {
            console.log(`🚀 Using ${preBinnedHexagons.length} pre-binned hexagons from backend`);
            return {
                type: 'FeatureCollection' as const,
                features: preBinnedHexagons.map((hex, idx) => ({
                    type: 'Feature' as const,
                    id: idx,
                    geometry: {
                        type: 'Polygon' as const,
                        coordinates: [hex.coordinates]
                    },
                    properties: {
                        value: hex.value,
                        count: hex.count,
                        color: getColorForValue(hex.value)
                    }
                }))
            };
        }

        // Fallback to client-side binning (GPS mode only)
        const gpsPoints = dataPoints?.filter(p => p.gpsLat != null && p.gpsLon != null && Number.isFinite(p.gpsLat) && Number.isFinite(p.gpsLon)) || [];
        if (gpsPoints.length === 0 || !geoJsonPreview?.features?.[0]?.geometry?.coordinates?.[0]) {
            return null;
        }

        console.log(`⚙️ Client-side binning ${gpsPoints.length} points into hexagons (GPS mode)`);

        // Calculate bounds from polygon
        const coords = geoJsonPreview.features[0].geometry.coordinates[0];
        const lons = coords.map((c: any) => c[0]);
        const lats = coords.map((c: any) => c[1]);
        const bounds = {
            minLat: Math.min(...lats),
            maxLat: Math.max(...lats),
            minLon: Math.min(...lons),
            maxLon: Math.max(...lons)
        };

        // Generate hexagon grid (convert cm to meters)
        const hexGrid = generateHexagonGrid(bounds, hexagonSize / 100);
        
        // Bin GPS points to hexagons with area polygon filter
        const areaPolygon: Array<[number, number]> = coords.map((c: any) => [c[0], c[1]]);
        const filledHexagons = binPointsToHexagons(
            hexGrid,
            gpsPoints.map(p => ({ lat: p.gpsLat!, lon: p.gpsLon!, value: p.value })),
            areaPolygon // Pass area polygon to filter hexagons
        );

        return {
            type: 'FeatureCollection' as const,
            features: filledHexagons.map((hex, idx) => ({
                type: 'Feature' as const,
                id: idx,
                geometry: {
                    type: 'Polygon' as const,
                    coordinates: [hex.coordinates]
                },
                properties: {
                    value: hex.value,
                    count: hex.count,
                    color: getColorForValue(hex.value)
                }
            }))
        };
    }, [mode, preBinnedHexagons, dataPoints, geoJsonPreview, minValue, maxValue, colorScheme, hexagonSize]);

    const renderGpsHeatmap = () => {
        const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN?.replace(/"/g, '') || '';

        return (
            <div className="report-static-heatmap-map-frame">
                <Map
                    ref={mapRefGps}
                    mapboxAccessToken={MAPBOX_TOKEN}
                    initialViewState={{
                        latitude: 51.5074,
                        longitude: -0.1278,
                        zoom: 13,
                        bearing: mapBearing,
                        pitch: 0
                    }}
                    style={{ width: '100%', height: '100%' }}
                    mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
                    onLoad={() => {
                        const map = mapRefGps.current;
                        if (!map) return;

                        // Fit bounds to polygon
                        if (geoJsonPreview?.features?.[0]?.geometry?.coordinates?.[0]) {
                            const coords = geoJsonPreview.features[0].geometry.coordinates[0];
                            const lngs = coords.map((c: any) => c[0]);
                            const lats = coords.map((c: any) => c[1]);
                            const bounds: [[number, number], [number, number]] = [
                                [Math.min(...lngs), Math.min(...lats)],
                                [Math.max(...lngs), Math.max(...lats)]
                            ];
                            map.fitBounds(bounds, {
                                padding: { top: 50, bottom: 50, left: 50, right: 50 },
                                duration: 800
                            });
                        }
                    }}
                >
                    {/* Hexagon heatmap layer */}
                    {hexagonGeoJson && (
                        <Source type="geojson" data={hexagonGeoJson}>
                            <Layer
                                id="hexagon-fill"
                                type="fill"
                                paint={{
                                    'fill-color': ['get', 'color'],
                                    'fill-opacity': heatmapOpacity
                                }}
                            />
                        </Source>
                    )}

                    {/* Polygon boundary */}
                    {geoJsonPreview && (
                        <Source type="geojson" data={geoJsonPreview}>
                            <Layer
                                id="area-boundary"
                                type="line"
                                paint={{
                                    'line-color': '#FFA500',
                                    'line-width': 2,
                                    'line-opacity': 0.8
                                }}
                            />
                            <Layer
                                id="area-fill"
                                type="fill"
                                paint={{
                                    'fill-color': '#FEE3AD',
                                    'fill-opacity': 0.1
                                }}
                            />
                        </Source>
                    )}
                </Map>
            </div>
        );
    };

    const renderTemplateView = () => {
        const hasData = (grid && grid.cells.length > 0) || 
                        (dataPoints && dataPoints.length > 0) ||
                        (value !== null && value !== undefined && !Number.isNaN(value));
        
        return (
            <div
                style={{
                    position: 'relative',
                    width: '100%',
                    height: 400,
                    overflow: 'hidden',
                    borderRadius: 8,
                    border: '1px solid #f0f0f0',
                    background: '#fafafa',
                }}
            >
                {templateImageUrl && (
                    <img
                        ref={imgRef}
                        src={templateImageUrl}
                        alt="Template"
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            pointerEvents: 'none',
                            opacity: templateOpacity,
                        }}
                        onLoad={() => setImgLoaded(true)}
                    />
                )}
                {!hasData && (
                    <div style={{
                        position: 'absolute',
                        top: 10,
                        left: 10,
                        background: 'rgba(255,255,255,0.9)',
                        padding: '8px 12px',
                        borderRadius: 4,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                    }}>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            No data available - showing template only
                        </Typography.Text>
                    </div>
                )}
                {hasData && grid && renderGridHeatmap()}
                {/* Template lines overlay - always on top at 100% opacity */}
                {templateLinesUrl && (
                    <img
                        ref={linesImgRef}
                        src={templateLinesUrl}
                        alt="Template Lines"
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            pointerEvents: 'none',
                            opacity: 1.0, // Always 100% opacity for field lines
                            zIndex: 10, // On top of everything
                        }}
                        onLoad={() => {
                            console.log('✅ [ReportHeatmap] Template lines image LOADED successfully:', templateLinesUrl);
                            setLinesImgLoaded(true);
                        }}
                        onError={(e) => {
                            console.error('❌ [ReportHeatmap] Template lines image FAILED to load:', {
                                url: templateLinesUrl,
                                error: e,
                                errorType: (e as any).type
                            });
                        }}
                    />
                )}
            </div>
        );
    };

    const locationMapVertices = useMemo(() => {
        if (!geoJsonPreview?.features?.[0]?.geometry?.coordinates?.[0]) {
            return [];
        }
        const coords = geoJsonPreview.features[0].geometry.coordinates[0];
        const trimmed = coords.length > 1 && 
            coords[0][0] === coords[coords.length - 1][0] && 
            coords[0][1] === coords[coords.length - 1][1]
            ? coords.slice(0, -1)
            : coords;
        
        return trimmed
            .filter((c: any) => c && c[0] !== undefined && c[1] !== undefined)
            .map((c: any) => ({ lng: c[0], lat: c[1] }));
    }, [geoJsonPreview]);

    const renderLocationMap = () => {
        if (!geoJsonPreview?.features?.[0]?.geometry?.coordinates?.[0]) {
            return null;
        }

        const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN?.replace(/"/g, '') || '';
        
        return (
            <div style={{ width: '100%', height: 400, borderRadius: 8, overflow: 'hidden' }}>
                <Map
                    mapboxAccessToken={MAPBOX_TOKEN}
                    initialViewState={{
                        latitude: 51.5074,
                        longitude: -0.1278,
                        zoom: 13,
                        bearing: mapBearing,
                        pitch: 0
                    }}
                    style={{ width: '100%', height: '100%' }}
                    mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
                    onLoad={(e) => {
                        const map = e.target;
                        if (!map) return;

                        const coords = geoJsonPreview.features[0].geometry.coordinates[0];
                        const lngs = coords.map((c: any) => c[0]);
                        const lats = coords.map((c: any) => c[1]);
                        const bounds: [[number, number], [number, number]] = [
                            [Math.min(...lngs), Math.min(...lats)],
                            [Math.max(...lngs), Math.max(...lats)]
                        ];
                        map.fitBounds(bounds, {
                            padding: { top: 50, bottom: 50, left: 50, right: 50 },
                            duration: 800,
                            bearing: mapBearing
                        });
                    }}
                >
                    {/* Template overlay if available */}
                    {templateImageUrl && (
                        <TemplateImageOverlay 
                            templateImageUrl={templateImageUrl}
                            vertices={locationMapVertices}
                            opacity={templateOpacity}
                        />
                    )}
                    
                    <Source type="geojson" data={geoJsonPreview}>
                        <Layer
                            id="location-boundary"
                            type="line"
                            paint={{
                                'line-color': '#FFA500',
                                'line-width': 3,
                                'line-opacity': 1
                            }}
                        />
                        <Layer
                            id="location-fill"
                            type="fill"
                            paint={{
                                'fill-color': '#FFA500',
                                'fill-opacity': 0.2
                            }}
                        />
                    </Source>
                </Map>
            </div>
        );
    };

    const renderStage = () => {
        // GPS mode
        if (mode === 'gps') {
            // If GPS area has a template, show both heatmap and template overlay
            if (templateImageUrl) {
                return (
                    <div className="report-static-heatmap-panels">
                        <div className="report-static-heatmap-panel">
                            <div className="report-static-heatmap-panel-label">Heatmap</div>
                            {renderGpsHeatmap()}
                        </div>
                        <div className="report-static-heatmap-panel">
                            <div className="report-static-heatmap-panel-label">Template overlay</div>
                            {renderLocationMap()}
                        </div>
                    </div>
                );
            }
            // GPS without template - just show heatmap
            return renderGpsHeatmap();
        }

        // Template mode - render template with optional grid overlay + location map
        return (
            <div className="report-static-heatmap-panels">
                <div className="report-static-heatmap-panel">
                    <div className="report-static-heatmap-panel-label">Template</div>
                    {renderTemplateView()}
                </div>
                <div className="report-static-heatmap-panel">
                    <div className="report-static-heatmap-panel-label">Location</div>
                    {renderLocationMap()}
                </div>
            </div>
        );
    };

    const content = (
        <div className="report-static-heatmap">
            <div className="report-static-heatmap-head">
                {title ? <div className="report-static-heatmap-title">{title}</div> : null}
            </div>

            <div className="report-static-heatmap-summary">
                <div className="report-static-heatmap-summary-main">
            {subtitle && (
                        <div className="report-static-heatmap-links">
                    {subtitle}
                </div>
            )}
            {value !== undefined && value !== null && (
                        <Typography.Text type="secondary" className="report-static-heatmap-range">
                        Avg: {formatNumber(value)}{unit ? ` ${unit}` : ''}
                        {' '}·{' '}Range: {formatNumber(minValue)} - {formatNumber(maxValue)}{unit ? ` ${unit}` : ''}
                        </Typography.Text>
            )}
                </div>
            </div>

            <div className="report-static-heatmap-stage">
                {renderStage()}
            </div>
        </div>
    );

    if (showCard) {
        return content;
    }

    return content;
};

// Template Image Overlay Component
const TemplateImageOverlay: React.FC<{
    templateImageUrl: string;
    vertices: Array<{ lng: number; lat: number }>;
    opacity?: number;
}> = ({ templateImageUrl, vertices, opacity = 0.5 }) => {
    const { current: map } = useMap();
    const [mapLoaded, setMapLoaded] = useState(false);

    // Track when map style is loaded
    useEffect(() => {
        if (!map) return;
        const mapInstance = map.getMap();
        
        if (mapInstance.isStyleLoaded()) {
            setMapLoaded(true);
        } else {
            const onLoad = () => {
                setMapLoaded(true);
                mapInstance.off('load', onLoad);
            };
            mapInstance.on('load', onLoad);
            return () => {
                mapInstance.off('load', onLoad);
            };
        }
    }, [map]);

    useEffect(() => {
        if (!map || !mapLoaded || !templateImageUrl || vertices.length < 4) return;

        const mapInstance = map.getMap();
        const layerId = 'report-template-overlay';
        const sourceId = 'report-template-source';

        // Remove existing overlay
        try {
            if (mapInstance.getLayer(layerId)) {
                mapInstance.removeLayer(layerId);
            }
            if (mapInstance.getSource(sourceId)) {
                mapInstance.removeSource(sourceId);
            }
        } catch (err) {
            console.warn('[ReportHeatmap] Error removing existing overlay:', err);
        }

        // Add overlay - vertices ordered [BL, BR, TR, TL], Mapbox needs [TL, TR, BR, BL]
        const bounds: [[number, number], [number, number], [number, number], [number, number]] = [
            [vertices[3].lng, vertices[3].lat],  // top-left
            [vertices[2].lng, vertices[2].lat],  // top-right
            [vertices[1].lng, vertices[1].lat],  // bottom-right
            [vertices[0].lng, vertices[0].lat]   // bottom-left
        ];

        mapInstance.addSource(sourceId, {
            type: 'image',
            url: templateImageUrl,
            coordinates: bounds
        });

        mapInstance.addLayer({
            id: layerId,
            type: 'raster',
            source: sourceId,
            paint: {
                'raster-opacity': opacity,
                'raster-brightness-min': 0,
                'raster-brightness-max': 1
            }
        });

        return () => {
            if (!mapInstance || typeof mapInstance.getLayer !== 'function') {
                return;
            }

            let styleLoaded = true;
            try {
                if (typeof mapInstance.isStyleLoaded === 'function') {
                    styleLoaded = mapInstance.isStyleLoaded();
                }
            } catch {
                styleLoaded = false;
            }
            if (!styleLoaded) {
                return;
            }
            
            try {
                if (mapInstance.getLayer(layerId)) {
                    mapInstance.removeLayer(layerId);
                }
                if (mapInstance.getSource(sourceId)) {
                    mapInstance.removeSource(sourceId);
                }
            } catch (err) {
                console.warn('[ReportHeatmap] Cleanup error (non-critical):', err);
            }
        };
    }, [map, mapLoaded, vertices, templateImageUrl, opacity]);

    return null;
};

export default ReportHeatmap;
